import { PADDLE_CONFIG, PADDLE_ENV } from '../config';

const PADDLE_SCRIPT_SRC = 'https://cdn.paddle.com/paddle/v2/paddle.js';
const PADDLE_SCRIPT_ID = 'paddle-js-v2';

let paddleLoadPromise = null;

function getPaddleCustomer(user) {
  const id = user?.paddleUserId ?? user?.paddleCustomerId ?? null;
  return typeof id === 'string' && id.startsWith('ctm_') ? { id } : {};
}

function loadPaddleScript() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (paddleLoadPromise) return paddleLoadPromise;

  paddleLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(PADDLE_SCRIPT_ID);

    const onLoad = () => resolve(window.Paddle);
    const onError = () => reject(new Error('Failed to load Paddle.js'));

    if (existingScript) {
      existingScript.addEventListener('load', onLoad, { once: true });
      existingScript.addEventListener('error', onError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = PADDLE_SCRIPT_ID;
    script.src = PADDLE_SCRIPT_SRC;
    script.async = true;
    script.onload = onLoad;
    script.onerror = onError;
    document.body.appendChild(script);
  });

  return paddleLoadPromise;
}

export async function initializeOrUpdatePaddle({ user = null, eventCallback = undefined } = {}) {
  if (typeof window === 'undefined') return null;

  const config = PADDLE_CONFIG[PADDLE_ENV];
  if (!config) {
    console.error(`Invalid Paddle environment: ${PADDLE_ENV}`);
    return null;
  }

  if (!config.token) {
    console.error(`Paddle token not found for environment: ${PADDLE_ENV}`);
    return null;
  }

  const Paddle = await loadPaddleScript();
  if (!Paddle) return null;

  const updatePayload = {
    pwCustomer: getPaddleCustomer(user),
  };

  if (eventCallback !== undefined) {
    updatePayload.eventCallback = eventCallback;
  }

  if (Paddle.Initialized) {
    Paddle.Update(updatePayload);
    return Paddle;
  }

  Paddle.Environment.set(config.environment);
  Paddle.Initialize({
    token: config.token,
    ...updatePayload,
  });

  return Paddle;
}

export function clearPaddleEventCallback() {
  if (typeof window === 'undefined') return;
  if (!window.Paddle?.Initialized) return;

  window.Paddle.Update({ eventCallback: null });
}
